import { createSlug } from './URLHelper'

const createNotePath = ({
  noteId,
  options,
  threadId,
  commentId,
  blockId = '',
}) => {
  options = options ? `/${options}` : ''
  const queryString = Object.entries({
    threadId,
    commentId,
  }).reduce((queryString, [key, value]) => {
    if (!value) {
      return queryString
    }

    const separator = queryString.length > 0 ? '&' : ''

    return `${queryString}${separator}${encodeURIComponent(
      key
    )}=${encodeURIComponent(value)}`
  }, '')

  const hash = blockId ? `#${blockId}` : ''

  return `/notes/${noteId}${options}${
    queryString.length > 0 ? `?${queryString}` : ''
  }${hash}`
}

const createChannelPath = channelId => `/app/channels/${channelId}`

export function getAbsolutePath(path) {
  if (['undefined', 'null'].includes(path)) {
    // We might end up with borked returnTo urls sometimes and we don't want this to
    // break the redirection. Since this param is a query one, those values are strings
    // let's check for them
    path = ''
  }

  return `${document.location.origin}${path}`
}

function getLoginPath() {
  return window.location.pathname.startsWith('/app')
    ? `/auth/login?returnTo=${window.location.pathname}`
    : '/auth/login'
}

const RoutesHelper = {
  home: () => '/app/root',
  absoluteHome: () => getAbsolutePath('/app'),
  notifications: () => '/app/notifications',
  login: () => getLoginPath(),
  requestResetPassword: () => '/auth/request-reset-password',
  publicShareNoteIndex: ({ token, title }) =>
    `/p/note/${token}/${createSlug(title)}`,
  app: () => '/app/root',
  ownerOnboarding: () => '/app/onboarding',
  settingsOrganization: ({ tabId, options, messageId }) =>
    `/app/settings/organization/${tabId}${options ? `/${options}` : ''}${
      messageId ? `?messageId=${messageId}` : ''
    }`,
  settingsAnalytics: () => `/app/settings/organization/analytics`,
  settingsOrganizationIndex: () => `/app/settings/organization/informations`,
  settingsOrganizationChannels: () => `/app/settings/organization/channels`,
  settingsOrganizationBilling: () => `/app/settings/organization/billing`,
  settingsOrganizationBillingInvoices: () =>
    `/app/settings/organization/billing/invoices`,
  settingsOrganizationBillingUpgrade: () =>
    `/app/settings/organization/billing/upgrade`,
  settingsOrganizationBillingPaymentMethod: () =>
    `/app/settings/organization/billing/payment-method`,
  settingsProfile: () => `/app/settings/organization/profile`,
  settingsUsers: () => `/app/settings/organization/users`,
  settingsAuthentication: () => `/app/settings/organization/authentication`,
  settingsIntegrations: () => `/app/settings/organization/integrations`,
  settingsIntegrationListApplications: () =>
    `/app/settings/organization/integrations/applications`,
  settingsIntegrationApplicationDetails: ({ applicationId, redirectUri }) => {
    let extraParam = ''

    if (redirectUri) {
      extraParam = `&redirect_uri=${redirectUri}`
    }

    return `/app/settings/organization/integrations/application?applicationId=${applicationId}${extraParam}`
  },

  settingsIntegrationInstallApplication: ({
    applicationId,
    redirectUri = undefined,
  }) => {
    let extra = ''

    if (redirectUri) {
      extra = `?redirectUri=${redirectUri}`
    }

    return `/api/integrations/install/${applicationId}${extra}`
  },

  settingsInvitations: options => {
    const route = `/app/settings/organization/invitations`
    return options ? `${route}?email=${options.email}` : route
  },
  absoluteSettingsInvitations: () =>
    getAbsolutePath(`/app/settings/organization/invitations`),
  noteShow: ({ noteId, options, threadId, commentId, blockId }) =>
    `/app${createNotePath({
      noteId,
      options,
      threadId,
      commentId,
      blockId,
    })}`,
  absoluteNoteShow: ({ noteId, options, threadId, commentId }) =>
    getAbsolutePath(
      `/app${createNotePath({
        noteId,
        options,
        threadId,
        commentId,
      })}`
    ),
  noteChannelIndex: ({ channelId }) => `${createChannelPath(channelId)}/notes`,
  channelShow: ({ channelId }) => `/app/channels/${channelId}`,
  absoluteChannelShow: ({ channelId }) =>
    getAbsolutePath(createChannelPath(channelId)),
  filterShow: ({ filterId }) => `/app/filters/${filterId}`,
  noteChannelShow: ({
    channelId,
    noteId,
    options = undefined,
    threadId = undefined,
    commentId = undefined,
    blockId = undefined,
  }) =>
    `${createChannelPath(channelId)}${createNotePath({
      noteId,
      options,
      threadId,
      commentId,
      blockId,
    })}`,
  noteTemplateShow: ({ noteId }) => `/app/templates/${noteId}`,
  noteDetached: ({ noteId }) => `/app/notes/${noteId}/detached`,
  noteFilterIndex: ({ filterId }) => `/app/filters/${filterId}/notes`,
  noteFilterShow: ({
    filterId,
    noteId,
    options = undefined,
    threadId = undefined,
  }) =>
    `/app/filters/${filterId}${createNotePath({
      noteId,
      options,
      threadId,
    })}`,
  templates: () => `/app/templates`,
  templateShow: ({ templateId }) => `/app/templates/${templateId}`,
  contactImport: ({ provider, apiEndpoint, apiToken }) =>
    `${apiEndpoint}/auth/${provider}/contacts?apiToken=${apiToken}`,
  googleDriveFileImport: ({ apiEndpoint, apiToken }) =>
    `${apiEndpoint}/auth/google/drive?apiToken=${apiToken}`,
  autoClosePopup: () => '/api/auto-close-popup',
  integrationUserAssociation({
    apiEndpoint,
    applicationClientId,
    onFinishedRedirectUrl,
    associationRedirectUrl,
  }) {
    return `${apiEndpoint}/integrations/${applicationClientId}/users/add?sliteRedirectUrl=${encodeURIComponent(
      onFinishedRedirectUrl
    )}&userAddRedirectUrl=${encodeURIComponent(associationRedirectUrl)}`
  },
}

export default RoutesHelper
